import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { agentHealthColor, statusColor } from '../../utils/monitoringStatus';

const WorkforcePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workDate, setWorkDate] = useState(moment().format('YYYY-MM-DD'));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const statusFilter = searchParams.get('status');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['monitoringLiveWorkforce', workDate],
    queryFn: () => apiClient.monitoring.getLiveWorkforce({ date: workDate }),
    refetchInterval: 30000,
  });

  const rows = useMemo(() => {
    const all = data?.data?.data || [];
    if (!statusFilter) return all;
    return all.filter((row: any) => row.status === statusFilter);
  }, [data, statusFilter]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <TextField
          label="Work Date"
          type="date"
          size="small"
          value={workDate}
          onChange={(e) => setWorkDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Status</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Coverage</TableCell>
              <TableCell>Computer</TableCell>
              <TableCell>Clock</TableCell>
              <TableCell>Work State</TableCell>
              <TableCell>Application</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Active Today</TableCell>
              <TableCell>Idle Today</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>Agent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={14}><CircularProgress size={20} /></TableCell></TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={14}>
                  <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>Retry</Button>}
                  >
                    Could not load the workforce list. This is a server error, not an empty day.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={14}>No workforce rows for this date.</TableCell></TableRow>
            ) : rows.map((row: any) => (
              <React.Fragment key={row.employee_id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => setExpandedId(expandedId === row.employee_id ? null : row.employee_id)}>
                      {expandedId === row.employee_id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell><Chip size="small" color={statusColor(row.status)} label={row.status} /></TableCell>
                  <TableCell>{row.employee_name}</TableCell>
                  <TableCell>{row.campaign || '—'}</TableCell>
                  <TableCell>{row.coverage || '—'}</TableCell>
                  <TableCell>{row.computer || '—'}</TableCell>
                  <TableCell>{row.clock_status || '—'}</TableCell>
                  <TableCell>{row.work_state || '—'}</TableCell>
                  <TableCell>{row.current_application || '—'}</TableCell>
                  <TableCell>{row.current_website || '—'}</TableCell>
                  <TableCell>{row.active_today_label}</TableCell>
                  <TableCell>{row.idle_today_label}</TableCell>
                  <TableCell>{row.last_activity_label || '—'}</TableCell>
                  <TableCell><Chip size="small" color={agentHealthColor(row.agent_health)} label={row.agent_health} /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={14} sx={{ py: 0, borderBottom: expandedId === row.employee_id ? undefined : 0 }}>
                    <Collapse in={expandedId === row.employee_id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom>Expanded Details</Typography>
                        <Typography variant="body2">Windows User: {row.windows_user || '—'} | Agent: {row.agent_version || '—'}</Typography>
                        <Typography variant="body2">Working Since: {row.working_since ? moment(row.working_since).format('h:mm A') : '—'}</Typography>
                        {row.expanded?.current_activity && (
                          <Typography variant="body2">
                            Current Activity: {row.expanded.current_activity.application} — {row.expanded.current_activity.window_title || row.expanded.current_activity.domain}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button size="small" onClick={() => navigate(`/workforce-monitoring/activity?employee_id=${row.employee_id}&date=${workDate}`)}>
                            View Timeline
                          </Button>
                          {row.device_id && (
                            <Button size="small" onClick={() => navigate(`/workforce-monitoring/computers/${row.device_id}?tab=diagnostics`)}>
                              Diagnostics
                            </Button>
                          )}
                          <Button size="small" onClick={() => navigate(`/workforce-monitoring/sessions?employee_id=${row.employee_id}&date=${workDate}`)}>
                            View Sessions
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WorkforcePage;
